import { useIdleTimer } from '@hooks/useIdleTimer';
import { useAuth } from '@/shared/providers/AuthContext';
import { useMassagerAuthContext } from '@/shared/providers/MassagerAuthContext';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const DEFAULT_IDLE_TIME = parseInt(import.meta.env.VITE_IDLE_TIME || '10790000');

type IdleTimerHandleProps = {
  isAuthenticated: boolean;
  onLogout: () => Promise<void> | void;
  loginPath: string;
};

function IdleTimerHandle({ isAuthenticated, onLogout, loginPath }: IdleTimerHandleProps) {
  const navigate = useNavigate();

  const hasLoggedOutRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  const idleTime = DEFAULT_IDLE_TIME;

  const [isIdle] = useIdleTimer(idleTime);

  const handleClickLogout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    // Redirect to login silently (no toast for idle timeout).
    navigate(loginPath, { replace: true });

    // Best effort API logout in background.
    try {
      await Promise.resolve(onLogout());
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [loginPath, navigate, onLogout]);

  useEffect(() => {
    if (isAuthenticated && hasLoggedOutRef.current) {
      hasLoggedOutRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isIdle || !isAuthenticated || hasLoggedOutRef.current) return;

    hasLoggedOutRef.current = true;
    handleClickLogout();
  }, [isIdle, isAuthenticated, handleClickLogout]);

  return null;
}

const ClinicIdleTimerImpl = () => {
  const { isAuthenticated, logout, clinic, authEntryType } = useAuth();
  const currentSite = window.location.pathname.match(/^\/([^/]+)(?:\/|$)/)?.[1];
  const siteType = clinic?.parent_site_type || clinic?.site_type || currentSite || 'medix';
  const loginPath = authEntryType === 'admin'
    ? `/${siteType}/admin/login`
    : `/${siteType}/login`;

  return <IdleTimerHandle isAuthenticated={isAuthenticated} onLogout={logout} loginPath={loginPath} />;
};

const SiteAdminIdleTimerImpl = () => {
  const { isAuthenticated, logout, currentSite } = useSiteAdminAuth();
  const siteType = currentSite || window.location.pathname.match(/^\/([^/]+)\/admin(?:\/|$)/)?.[1] || 'medix';
  const loginPath = `/${siteType}/admin/login`;

  return <IdleTimerHandle isAuthenticated={isAuthenticated} onLogout={logout} loginPath={loginPath} />;
};

const MassagerIdleTimerImpl = () => {
  const { isAuthenticated, logout, clinic } = useMassagerAuthContext();
  const currentSite = window.location.pathname.match(/^\/([^/]+)\/massager(?:\/|$)/)?.[1];
  const siteType = clinic?.parent_site_type || clinic?.site_type || currentSite || 'medix';
  const loginPath = `/${siteType}/massager/login`;

  return <IdleTimerHandle isAuthenticated={isAuthenticated} onLogout={logout} loginPath={loginPath} />;
};

type PortalIdleTimerHandleProps = {
  portal: 'clinic' | 'site-admin' | 'massager';
};

/**
 * Single public component reused by all three portals.
 */
function PortalIdleTimerHandle({ portal }: PortalIdleTimerHandleProps) {
  if (portal === 'site-admin') {
    return <SiteAdminIdleTimerImpl />;
  }
  if (portal === 'massager') {
    return <MassagerIdleTimerImpl />;
  }

  return <ClinicIdleTimerImpl />;
}

export default PortalIdleTimerHandle;
